import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { directoryApi, type PersonalDirectoryEntry } from "@/lib/api";
import { Plus, Search, Mail, Phone, Truck, UserRound, Pencil, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

type PersonalStatus = PersonalDirectoryEntry["status"];

const statusTone: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
};

type Form = { name: string; email: string; phone: string; status: PersonalStatus };
const empty: Form = { name: "", email: "", phone: "", status: "active" };

export default function UsersPersonal() {
  const token = useAuth((s) => s.session?.accessToken);
  const [items, setItems] = useState<PersonalDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | PersonalStatus>("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PersonalDirectoryEntry | null>(null);
  const [form, setForm] = useState<Form>(empty);

  useEffect(() => {
    let cancelled = false;
    directoryApi
      .personal(token)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => toast.error("Failed to load personal accounts"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(() => items.filter((p) => {
    if (status !== "all" && p.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      p.name.toLowerCase().includes(s) ||
      (p.email?.toLowerCase().includes(s) ?? false) ||
      (p.phone?.includes(s) ?? false)
    );
  }), [items, q, status]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p: PersonalDirectoryEntry) => {
    setEditing(p);
    setForm({ name: p.name, email: p.email ?? "", phone: p.phone ?? "", status: p.status });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      return toast.error("Name and email or phone required");
    }
    try {
      if (editing) {
        const updated = await directoryApi.updatePersonal(editing.id, {
          full_name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          status: form.status,
        }, token);
        setItems((cur) => cur.map((p) => (p.id === editing.id ? updated : p)));
        toast.success("Personal account updated");
      } else {
        const created = await directoryApi.createPersonal({
          full_name: form.name,
          email: form.email || undefined,
          phone: form.phone || undefined,
        }, token);
        setItems((cur) => [created, ...cur]);
        toast.success("Personal account added");
      }
      setOpen(false);
    } catch {
      toast.error(editing ? "Could not update account" : "Could not add account");
    }
  };

  const counts = {
    total: items.length,
    active: items.filter((p) => p.status === "active").length,
    suspended: items.filter((p) => p.status === "suspended").length,
  };

  return (
    <>
      <Topbar
        title="Personal accounts"
        subtitle={`${counts.total} self-service owners · ${counts.active} active`}
        actions={<Button size="sm" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Add account</Button>}
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          {(["total", "active", "suspended"] as const).map((k) => (
            <div key={k} className="surface-card p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground capitalize">{k}</div>
              <div className="mt-1 text-2xl font-bold">{counts[k]}</div>
            </div>
          ))}
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone…" className="pl-9" />
            </div>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Owner</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Vehicles</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6}><LoadingBlock label="Loading personal accounts…" /></TableCell></TableRow>
              ) : (
                <>
                  {filtered.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          <UserRound className="h-3.5 w-3.5 text-muted-foreground" /> {p.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{p.id} · joined {new Date(p.created_at).toLocaleDateString()}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3 text-muted-foreground" />{p.email ?? "—"}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{p.phone ?? "—"}</div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {p.vehicle_count}</span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", statusTone[p.status] ?? "bg-muted text-muted-foreground border-border")}>
                          {p.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" asChild aria-label="Dashboard"><Link to={`/app/users/personal/${p.id}`}><LayoutDashboard className="h-3.5 w-3.5" /></Link></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No personal accounts match.</TableCell></TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "Add personal account"}</DialogTitle>
            <DialogDescription>Self-service vehicle owner — no deliveries or team access.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            {editing ? (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as PersonalStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
